import { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Search, X, Pencil, Trash2, SlidersHorizontal, Table2, Rows3, Droplets } from 'lucide-react';
import { FormModal } from '../../components/ui/FormModal';
import { Button, Toast } from '../../components/ui/FormField';
import { Field, TextInput, SelectInput, PrimaryButton, GhostButton } from '../../components/ui/Field';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { IconButton, Cell, PaginationControls, ViewModeSwitch } from '../../components/ui/grid';
import { useCrudPage } from '../../hooks/useCrudPage';
import { useAutoCreate } from '../../hooks/useAutoCreate';
import { usePageHeader } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { siteApi, companyApi, wellApi } from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import type { Site, Company } from '../../types/api';
import { COUNTRIES, getStatesProvinces } from '../../data/geo-data';

type SiteView = 'comfortable' | 'compact';
const VIEW_MODE_KEY = 'sites.viewMode';
function loadSiteView(): SiteView {
  try { const v = localStorage.getItem(VIEW_MODE_KEY); if (v === 'compact' || v === 'comfortable') return v; } catch { /* ignore */ }
  return 'comfortable';
}

function regionText(country?: string, state?: string): string {
  const parts = [state?.trim(), country?.trim()].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

// ── Validation (unchanged from the old UI) ───────────────────
const emptySite: Partial<Site> = { siteName: '', country: '', stateProvince: '', companyId: 0 };

interface SiteErrors {
  companyId?: string;
  siteName?: string;
  country?: string;
  stateProvince?: string;
}

function validateSite(form: Partial<Site>): SiteErrors {
  const e: SiteErrors = {};
  if (!form.companyId) e.companyId = 'Company is required';
  if (!form.siteName?.trim()) e.siteName = 'Surface Location is required';
  if (!form.country?.trim()) e.country = 'Country is required';
  if (!form.stateProvince?.trim()) e.stateProvince = 'State/Province is required';
  return e;
}

export function SitesPage() {
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const isCompanyAdmin = authUser?.role === 'CompanyAdmin';
  const canManageSites = isSuperAdmin || isCompanyAdmin;
  const recordActivity = useActivityStore((s) => s.record);

  const crud = useCrudPage<Site>({ fetchFn: siteApi.getAll });
  const [form, setForm] = useState<Partial<Site>>(emptySite);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [errors, setErrors] = useState<SiteErrors>({});
  const [submitted, setSubmitted] = useState(false);

  // View density (persisted) + client-side filters
  const [viewMode, setViewModeRaw] = useState<SiteView>(loadSiteView);
  const setViewMode = (v: SiteView) => { setViewModeRaw(v); try { localStorage.setItem(VIEW_MODE_KEY, v); } catch { /* ignore */ } };
  const compact = viewMode === 'compact';
  const [companyFilter, setCompanyFilter] = useState<number | ''>('');
  const [countryFilter, setCountryFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  useEffect(() => {
    companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? [])).catch(() => {});
  }, []);

  const openEdit = (item: Site) => { setForm({ ...item }); setErrors({}); setSubmitted(false); crud.openEdit(item); };
  const openCreate = () => {
    setForm({ ...emptySite, companyId: isSuperAdmin ? 0 : Number(authUser?.companyId ?? 0) });
    setErrors({}); setSubmitted(false); crud.openCreate();
  };
  useAutoCreate(openCreate, canManageSites);

  const handleSave = async () => {
    setSubmitted(true);
    const e = validateSite(form);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSaving(true);
    try {
      const label = form.siteName?.trim() || 'Location';
      if (crud.editingItem) {
        await siteApi.update(form);
        crud.showToast('Surface Location updated successfully');
        recordActivity({ type: 'update', entity: 'Locations', label, user: authUser?.username });
      } else {
        await siteApi.create(form);
        crud.showToast('Surface Location created successfully');
        recordActivity({ type: 'create', entity: 'Locations', label, user: authUser?.username });
      }
      crud.closeModal();
      crud.load();
    } catch {
      crud.showToast('Failed to save Surface Location', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (site: Site) => {
    // Guard: a location with wells cannot be deleted (unchanged behavior).
    try {
      const wellsRes = await wellApi.getAllBySiteId(site.id, { limit: 1 });
      if ((wellsRes.entities?.length ?? 0) > 0) {
        crud.showToast('This Location has valid wells. Please delete all wells before deleting this location.', 'error');
        return;
      }
    } catch { /* proceed if check fails */ }
    if (!confirm(`Delete Surface Location "${site.siteName}"?`)) return;
    try {
      await siteApi.delete({ id: site.id });
      crud.showToast('Surface Location deleted');
      recordActivity({ type: 'delete', entity: 'Locations', label: site.siteName?.trim() || `Location #${site.id}`, user: authUser?.username });
      crud.load();
    } catch {
      crud.showToast('Failed to delete Surface Location', 'error');
    }
  };

  const set = (key: string, value: string | number) => setForm((prev) => ({ ...prev, [key]: value }));

  const goWells = (site: Site) =>
    navigate(`/dashboard/wells?siteId=${site.id}&siteName=${encodeURIComponent(site.siteName ?? '')}`);

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  // Publish title + primary action. Deps keep openCreate current as auth hydrates.
  usePageHeader(
    'Surface Locations',
    canManageSites ? <Button onClick={openCreate}><Plus size={16} /> Add Surface Location</Button> : null,
    [canManageSites, isSuperAdmin, authUser?.companyId],
  );

  // Distinct countries / states from the loaded page (powers the filter selects).
  const availableCountries = useMemo(() => {
    const s = new Set<string>();
    crud.data.forEach((x) => x.country?.trim() && s.add(x.country.trim()));
    return Array.from(s).sort();
  }, [crud.data]);
  const availableStates = useMemo(() => {
    const s = new Set<string>();
    crud.data.forEach((x) => {
      if (countryFilter && (x.country ?? '').trim() !== countryFilter) return;
      if (x.stateProvince?.trim()) s.add(x.stateProvince.trim());
    });
    return Array.from(s).sort();
  }, [crud.data, countryFilter]);

  const filteredData = useMemo(() => crud.data.filter((x) => {
    if (companyFilter !== '' && (x.companyId ?? null) !== companyFilter) return false;
    if (countryFilter && (x.country ?? '').trim() !== countryFilter) return false;
    if (stateFilter && (x.stateProvince ?? '').trim() !== stateFilter) return false;
    return true;
  }), [crud.data, companyFilter, countryFilter, stateFilter]);

  const filtersActive = companyFilter !== '' || !!countryFilter || !!stateFilter;
  const clearFilters = () => { setCompanyFilter(''); setCountryFilter(''); setStateFilter(''); };

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  // Column template — varies by role (Company column) and density.
  const colTemplate = compact
    ? (isSuperAdmin
        ? 'minmax(160px,1.3fr) minmax(220px,1.7fr) minmax(200px,1.4fr)'
        : 'minmax(240px,1.8fr) minmax(220px,1.4fr)')
    : (isSuperAdmin
        ? 'minmax(150px,1.2fr) minmax(180px,1.5fr) minmax(120px,1fr) minmax(120px,1fr) 190px'
        : 'minmax(200px,1.6fr) minmax(120px,1fr) minmax(120px,1fr) 168px');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div style={{ background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10, width: 'fit-content', minWidth: '100%' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)', borderRadius: 8, width: 280 }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search surface locations…"
              value={crud.searchText}
              onChange={(e) => { crud.setSearchText(e.target.value); crud.setOffset(0); }}
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13.5, flex: 1, fontFamily: 'var(--font-body)', color: 'var(--color-text)', width: '100%' }}
            />
            {crud.searchText && (
              <button onClick={() => { crud.setSearchText(''); crud.setOffset(0); }} aria-label="Clear search" title="Clear search"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 9, padding: 0, background: 'transparent', border: 'none', color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0 }}>
                <X size={13} />
              </button>
            )}
          </div>

          <SitesFilterPopover
            isSuperAdmin={isSuperAdmin}
            companyFilter={companyFilter}
            countryFilter={countryFilter}
            stateFilter={stateFilter}
            companies={companies}
            availableCountries={availableCountries}
            availableStates={availableStates}
            onChangeCompany={setCompanyFilter}
            onChangeCountry={(v) => { setCountryFilter(v); if (v && stateFilter && availableStates.indexOf(stateFilter) === -1) setStateFilter(''); }}
            onChangeState={setStateFilter}
            onClear={clearFilters}
            activeCount={(companyFilter !== '' ? 1 : 0) + (countryFilter ? 1 : 0) + (stateFilter ? 1 : 0)}
          />

          {filtersActive && (
            <button onClick={clearFilters} style={{ fontSize: 12, color: 'var(--color-accent)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 8px', borderRadius: 5, fontFamily: 'var(--font-body)', fontWeight: 500 }}>
              Clear all
            </button>
          )}

          <div style={{ flex: 1 }} />

          <ViewModeSwitch
            value={viewMode}
            onChange={setViewMode}
            options={[{ id: 'comfortable', tip: 'Comfortable view', Icon: Table2 }, { id: 'compact', tip: 'Compact view', Icon: Rows3 }]}
          />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading ? 'Loading…' : filtersActive ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total` : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header + rows */}
        <div >
          <div
            className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
            style={{
              display: 'grid',
              gridTemplateColumns: colTemplate,
              columnGap: 0,
              padding: 0,
              alignItems: 'stretch',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
              position: 'sticky',
              top: 72,
              zIndex: 10,
              fontSize: 14,
              letterSpacing: 0.3,
              color: 'var(--color-text)',
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
            }}
          >
            {isSuperAdmin && <SortHeader label="Company" col="companyName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />}
            <SortHeader label="Surface Location" col="SiteName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            {compact ? (
              <SortHeader label="Region" col="Country" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            ) : (
              <>
                <SortHeader label="Country" col="Country" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
                <SortHeader label="State/Province" col="StateProvince" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
                <div style={{ textAlign: 'right' }}>Actions</div>
              </>
            )}
          </div>

          {crud.loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>Loading…</div>
          ) : filteredData.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
              <MapPin size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <div style={{ fontSize: 14, marginBottom: 4 }}>{filtersActive ? 'No locations match these filters' : 'No locations found'}</div>
              <div style={{ fontSize: 12 }}>{filtersActive ? 'Try clearing the filters.' : 'Add a new location to get started.'}</div>
            </div>
          ) : (
            <div>
              {filteredData.map((site, i) => (
                <SiteRow
                  key={site.id ?? i}
                  site={site}
                  compact={compact}
                  isSuperAdmin={isSuperAdmin}
                  canManage={canManageSites}
                  colTemplate={colTemplate}
                  isLast={i === filteredData.length - 1}
                  onWells={() => goWells(site)}
                  onEdit={() => openEdit(site)}
                  onDelete={() => handleDelete(site)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination footer */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--color-text-dim)', background: 'var(--color-panel-alt)', borderRadius: '0 0 10px 10px' }}>
          <div>
            Showing <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span> locations
          </div>
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPage={(p) => crud.setOffset((p - 1) * crud.pageSize)} />
        </div>
      </div>

      {/* Form modal — flat (no section headings) */}
      <FormModal
        open={crud.modalOpen}
        onClose={crud.closeModal}
        title={crud.editingItem ? 'Edit Surface Location' : 'New Surface Location'}
        icon={<MapPin size={20} />}
        footer={
          <>
            <GhostButton onClick={crud.closeModal} disabled={saving}>Cancel</GhostButton>
            <PrimaryButton onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : crud.editingItem ? 'Save changes' : 'Add Surface Location'}
            </PrimaryButton>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Company" required error={submitted ? errors.companyId : undefined}>
              {isSuperAdmin ? (
                <SelectInput value={form.companyId ?? ''} error={submitted && !!errors.companyId} onChange={(e) => set('companyId', Number(e.target.value))}>
                  <option value="">-- Select Company --</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                </SelectInput>
              ) : (
                <TextInput value={companies.find(c => c.id === form.companyId)?.companyName ?? `Company #${form.companyId}`} readOnly style={{ background: 'var(--color-panel-alt)', color: 'var(--color-text-mid)' }} />
              )}
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Surface Location" required error={submitted ? errors.siteName : undefined}>
              <TextInput value={form.siteName ?? ''} placeholder="e.g. Edson 4-12 pad" error={submitted && !!errors.siteName} onChange={(e) => set('siteName', e.target.value)} />
            </Field>
          </div>
          <Field label="Country" required error={submitted ? errors.country : undefined}>
            <SelectInput value={form.country ?? ''} error={submitted && !!errors.country} onChange={(e) => { set('country', e.target.value); set('stateProvince', ''); }}>
              <option value="">-- Select Country --</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </SelectInput>
          </Field>
          <Field label="State/Province" required error={submitted ? errors.stateProvince : undefined}>
            {getStatesProvinces(form.country ?? '').length > 0 ? (
              <SelectInput value={form.stateProvince ?? ''} error={submitted && !!errors.stateProvince} onChange={(e) => set('stateProvince', e.target.value)}>
                <option value="">-- Select State/Province --</option>
                {getStatesProvinces(form.country ?? '').map((s) => <option key={s} value={s}>{s}</option>)}
              </SelectInput>
            ) : (
              <TextInput value={form.stateProvince ?? ''} placeholder="Enter State/Province" error={submitted && !!errors.stateProvince} onChange={(e) => set('stateProvince', e.target.value)} />
            )}
          </Field>
        </div>
      </FormModal>

      {crud.toast && <Toast message={crud.toast.message} type={crud.toast.type} onClose={() => crud.setToast(null)} />}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────

function WellsChip({ onClick }: { onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title="View wells"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, height: 36, padding: '0 12px',
        fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-body)', cursor: 'pointer',
        color: '#fff',
        background: hover ? 'var(--color-accent-hover)' : 'var(--color-accent)',
        border: `1px solid ${hover ? 'var(--color-accent-hover)' : 'var(--color-accent)'}`, borderRadius: 8, whiteSpace: 'nowrap',
        transition: 'background 120ms, border-color 120ms, color 120ms',
      }}
    >
      <Droplets size={13} /> Wells
    </button>
  );
}

function SiteRow({
  site,
  compact,
  isSuperAdmin,
  canManage,
  colTemplate,
  isLast,
  onWells,
  onEdit,
  onDelete,
}: {
  site: Site;
  compact: boolean;
  isSuperAdmin: boolean;
  canManage: boolean;
  colTemplate: string;
  isLast: boolean;
  onWells: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const rowBg = hover ? 'var(--color-panel-alt)' : (compact ? 'var(--color-panel)' : 'transparent');

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
      style={{
        display: 'grid',
        gridTemplateColumns: colTemplate,
        columnGap: 0,
        padding: 0,
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'stretch',
        fontSize: 14,
        position: compact ? 'relative' : undefined,
        background: rowBg,
        transition: 'background 120ms',
      }}
    >
      {isSuperAdmin && <Cell ellipsis>{site.company?.companyName ?? '—'}</Cell>}
      <Cell ellipsis style={{ fontWeight: 500 }}>{site.siteName || '—'}</Cell>
      {compact ? (
        <Cell ellipsis>{regionText(site.country, site.stateProvince)}</Cell>
      ) : (
        <>
          <Cell ellipsis>{site.country?.trim() || '—'}</Cell>
          <Cell ellipsis>{site.stateProvince?.trim() || '—'}</Cell>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
            <WellsChip onClick={onWells} />
            {canManage && (
              <>
                <IconButton title="Edit" onClick={onEdit}><Pencil size={17} /></IconButton>
                <IconButton title="Delete" onClick={onDelete} hoverDanger><Trash2 size={17} /></IconButton>
              </>
            )}
          </div>
        </>
      )}

      {compact && (
        <div
          style={{
            position: 'absolute', right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', gap: 6,
            paddingRight: 14, paddingLeft: 56,
            background: `linear-gradient(90deg, transparent 0%, ${rowBg} 32%, ${rowBg} 100%)`,
            opacity: hover ? 1 : 0, pointerEvents: hover ? 'auto' : 'none', transition: 'opacity 150ms', zIndex: 5,
          }}
        >
          <IconButton title="View wells" onClick={onWells} size={30}><Droplets size={15} /></IconButton>
          {canManage && (
            <>
              <IconButton title="Edit" onClick={onEdit} size={30}><Pencil size={15} /></IconButton>
              <IconButton title="Delete" onClick={onDelete} hoverDanger size={30}><Trash2 size={15} /></IconButton>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Filter popover — Country / State (+ Company for SuperAdmin).
function SitesFilterPopover({
  isSuperAdmin,
  companyFilter,
  countryFilter,
  stateFilter,
  companies,
  availableCountries,
  availableStates,
  onChangeCompany,
  onChangeCountry,
  onChangeState,
  onClear,
  activeCount,
}: {
  isSuperAdmin: boolean;
  companyFilter: number | '';
  countryFilter: string;
  stateFilter: string;
  companies: Company[];
  availableCountries: string[];
  availableStates: string[];
  onChangeCompany: (v: number | '') => void;
  onChangeCountry: (v: string) => void;
  onChangeState: (v: string) => void;
  onClear: () => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const POP_WIDTH = 320;

  const updateCoords = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords({ top: rect.bottom + 8, left: Math.max(12, Math.min(rect.left, window.innerWidth - POP_WIDTH - 12)) });
  }, []);

  useLayoutEffect(() => { if (open) updateCoords(); }, [open, updateCoords]);
  useEffect(() => {
    if (!open) return;
    const onScroll = () => updateCoords();
    const onDown = (e: MouseEvent) => { const t = e.target as Node; if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return; setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', updateCoords);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', updateCoords);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updateCoords]);

  const isActive = activeCount > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 12px', fontSize: 13, fontWeight: 500,
          fontFamily: 'var(--font-body)', cursor: 'pointer',
          background: open ? 'var(--color-panel-alt)' : 'var(--color-panel)',
          color: isActive ? 'var(--color-accent)' : 'var(--color-text-mid)',
          border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`, borderRadius: 7, transition: 'all 120ms',
        }}
      >
        <SlidersHorizontal size={14} />
        Filter
        {isActive && (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: 'var(--color-accent)', color: '#fff', fontSize: 10.5, fontWeight: 700, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
            {activeCount}
          </span>
        )}
      </button>

      {open && coords && createPortal(
        <div ref={popRef} style={{ position: 'fixed', top: coords.top, left: coords.left, width: POP_WIDTH, background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10, boxShadow: '0 12px 32px rgba(15,23,32,0.12)', zIndex: 100, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.2, color: 'var(--color-text-dim)', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>Filter by</div>
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-dim)', borderRadius: 4 }}>
              <X size={14} />
            </button>
          </div>

          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {isSuperAdmin && (
              <PopoverField label="Company">
                <FilterSelect value={companyFilter === '' ? '' : String(companyFilter)} onChange={(v) => onChangeCompany(v === '' ? '' : Number(v))} placeholder="All companies" options={companies.map((c) => ({ value: String(c.id), label: c.companyName }))} />
              </PopoverField>
            )}
            <PopoverField label="Country">
              <FilterSelect value={countryFilter} onChange={onChangeCountry} placeholder="All countries" options={availableCountries.map((c) => ({ value: c, label: c }))} />
            </PopoverField>
            <PopoverField label="State / Province">
              <FilterSelect value={stateFilter} onChange={onChangeState} placeholder={availableStates.length ? 'All states' : '— select country first —'} options={availableStates.map((s) => ({ value: s, label: s }))} disabled={availableStates.length === 0} />
            </PopoverField>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid var(--color-border)', background: 'var(--color-panel-alt)' }}>
            <button onClick={onClear} disabled={activeCount === 0} style={{ fontSize: 12, color: activeCount === 0 ? 'var(--color-text-dim)' : 'var(--color-accent)', background: 'transparent', border: 'none', cursor: activeCount === 0 ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)', fontWeight: 500, padding: '4px 4px' }}>Clear filters</button>
            <button onClick={() => setOpen(false)} style={{ fontSize: 12.5, fontWeight: 500, background: 'var(--color-accent)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Apply</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function PopoverField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11.5, color: 'var(--color-text-mid)', fontFamily: 'var(--font-body)', fontWeight: 500 }}>{label}</span>
      {children}
    </label>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  disabled?: boolean;
}) {
  const hasValue = !!value;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: '100%', height: 34, padding: '0 30px 0 11px', fontSize: 13, fontFamily: 'var(--font-body)',
        color: hasValue ? 'var(--color-text)' : 'var(--color-text-mid)',
        background: `var(--color-panel) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1l4 4 4-4' stroke='%238894a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat right 11px center`,
        border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 7, appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', outline: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, fontWeight: hasValue ? 500 : 400, transition: 'border-color 120ms',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
